/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceDescription } from "@adapptio/lib-core";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { TELEMETRY_EVENTS } from "./TelemetryConstants";
import { ITelemetryEvent } from "./TelemetryInterfaces";

declare global {
	interface Window {
		gtag: (...args: any[]) => void;
	}
}

/**
 * Service options
 */
export interface ITelemetryServiceOpts {
	/** Interval in ms to track editor activity */
	activityInterval: number;
}

/**
 * Service to collect and reporty editor telemetry
 */
export class TelemetryService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** Tracker timer */
	private trackerInterval: ReturnType<typeof setInterval>;

	/**
	 * Telemetry Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(dc: IEditor, private opts: ITelemetryServiceOpts) {
		this.logger = dc.get("logger").facility("TelemetryService");
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		this.trackerInterval = setInterval(() => {
			this.trackActivity();
		}, this.opts.activityInterval);
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		if (this.trackerInterval) {
			clearTimeout(this.trackerInterval);
			this.trackerInterval = undefined;
		}
	}

	/**
	 * Reports an invalid blueprint - eg. parse error
	 *
	 * @param resourceDescriptor Resource descriptor
	 */
	public reportInvalidBlueprint(resourceDescriptor: IResourceDescription): void {
		// @todo
	}

	/**
	 * Tracks an event
	 *
	 * @param eventName Event name
	 * @param eventParams Event params
	 */
	public trackEvent(event: ITelemetryEvent): void {
		this.logger.debug("Track event:", event);

		try {
			if (window.gtag) {
				window.gtag("event", event.eventName, event.eventParams);
			}
		} catch (err) {
			this.logger.warn("Failed to track event:", event);
		}
	}

	/**
	 * Tracks that editor is still being used
	 */
	private trackActivity(): void {
		this.trackEvent(TELEMETRY_EVENTS.lifecycle_editor_alive);
	}
}
